import { ChangeDetectionStrategy, Component, computed, inject, signal, WritableSignal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, map, take, tap } from 'rxjs';
import { CoreStore } from '@core/store/core.store';
import { authPageEvents } from '@core/store/core.events';
import { injectDispatch } from '@ngrx/signals/events';
import { ProfileModalComponent } from '@core/modals/profile-modal/profile-modal.component';
import { ModalViewerService } from '@nesea/ngx-ui-kit/modal';
import { ConfirmModalComponent, IConfirmModalOutput } from '@shared/components/confirm-modal/confirm-modal.component';
import { ConfigService } from "@core/services/config.service";
import { SupportModalComponent } from '@core/modals/support-modal/support-modal.component';
import { SpinnerComponent } from '@nesea/ngx-ui-kit/spinner';
import { HeaderComponent } from '@nesea/ngx-ui-kit/header';
import { BreadcrumbComponent } from '@nesea/ngx-ui-kit/breadcrumb';
import { NgClass } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { IHeaderAction } from '@nesea/ngx-ui-kit/shared';

@Component({
  selector: 'nsf-main-container',
  templateUrl: './main-container.component.html',
  styleUrl: './main-container.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    NgClass,
    BreadcrumbComponent,
    HeaderComponent,
    SpinnerComponent
  ]
})
export class MainContainerComponent {

  headerActions: IHeaderAction[] = [
    { label: 'MODAL.SUPPORT.TITLE', icon: 'support_agent', onClick: () => this.onHelpClick() },
  ];

  private _configService: ConfigService = inject(ConfigService);
  private _coreStore = inject(CoreStore);
  private _dispatch = injectDispatch(authPageEvents);
  private _router: Router = inject(Router);
  private _modalViewerService: ModalViewerService = inject(ModalViewerService);

  readonly environment: WritableSignal<string> = signal(this._configService.env);
  readonly version: WritableSignal<string> = signal(this._configService.version);

  readonly user = this._coreStore.user;

  readonly currentUrl = toSignal(
    this._router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects)
    ),
    { initialValue: this._router.url }
  );

  readonly isLogin = computed(() => this.currentUrl().includes('login'));
  readonly isErrorPage = computed(() => this.currentUrl().includes('error-page'));

  onBrandClick(): void {
    this._router.navigate(['']);
  }

  onProfileClick(): void {
    this._modalViewerService.open(ProfileModalComponent, {
      size: 'lg',
      title: 'HEADER.PROFILE.MODAL.TITLE',
      data: { user: this._coreStore.user(), timebank: this._coreStore.timebankTime() }
    }).pipe(
      take(1)
    ).subscribe();
  }

  onLogout(): void {
    this._modalViewerService.open(ConfirmModalComponent, { size: 'md', title: 'MODAL.CONFIRM.TITLE', titleUppercase: false, data: { message: 'MODAL.LOGOUT.MESSAGE' } }).pipe(
      take(1),
      filter((output: IConfirmModalOutput) => output?.outcome),
      tap(() => this._dispatch.logoutRequested())
    ).subscribe();
  }

  onHelpClick(): void {
    this._modalViewerService.open(SupportModalComponent, {
      size: 'md',
      title: 'MODAL.SUPPORT.TITLE',
      subtitle: 'MODAL.SUPPORT.SUBTITLE',
      titleUppercase: false,
      data: {
        message: 'MODAL.SUPPORT.MESSAGE',
        email: this._configService.config.contact?.technicalSupportEmail ?? ''
      }
    }).pipe(
      take(1),
    ).subscribe();
  }

}
